/**
 * Recovery for a lazy chunk the browser could not fetch.
 *
 * Vite code-splits every lazy route, so a route change fetches JS and CSS that the
 * running page did not load at boot. When one of those fetches fails — a deploy landed
 * while the tab sat open, CloudFront answered a stale request, the network blipped — the
 * dynamic `import()` rejects, React's lazy boundary re-throws, and the operator lands on
 * the "An unexpected error has occurred" page with no way forward but a manual refresh
 * (PORTAL-WEB-X8, `Unable to preload CSS for .../tileSources-<hash>.css`).
 *
 * Vite fires `vite:preloadError` for exactly this, before the rejection propagates, so
 * the page can reload itself and pick up the current asset manifest. The reload loses
 * whatever was on screen, but so does the crash page it replaces — by the time the error
 * boundary catches, the tree is already unmounted.
 */

/**
 * How long after an automatic reload a second preload failure is treated as real.
 *
 * One reload fixes a page holding a stale manifest. A second failure right behind it
 * means the asset is genuinely unreachable, and reloading again would spin the operator
 * in a loop with no error message to report — worse than the crash page. So the second
 * one is left to propagate.
 */
export const PRELOAD_RELOAD_COOLDOWN_MS = 60_000;

/** Where the last automatic reload is recorded. Session-scoped: a fresh tab starts fresh. */
const LAST_RELOAD_KEY = "rogo.preloadReloadAt";

/** True when an automatic reload is worth trying — see `PRELOAD_RELOAD_COOLDOWN_MS`. */
export function shouldReloadAfterPreloadError(now: number, lastReloadAt: number | null): boolean {
	return lastReloadAt === null || now - lastReloadAt >= PRELOAD_RELOAD_COOLDOWN_MS;
}

/** The recorded reload time, or null when there is none (or storage is unavailable —
 *  a blocked sessionStorage should not cost the operator the one reload that fixes this). */
function readLastReloadAt(): number | null {
	try {
		const raw = window.sessionStorage.getItem(LAST_RELOAD_KEY);
		if (raw === null) return null;
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	} catch {
		return null;
	}
}

function recordReloadAt(now: number): void {
	try {
		window.sessionStorage.setItem(LAST_RELOAD_KEY, String(now));
	} catch {
		// Storage blocked. The reload still happens; only the loop guard is lost, and the
		// browser's own failed-request caching stops a tight spin.
	}
}

/**
 * Listen for `vite:preloadError` and reload once to pick up the current assets.
 * Call at boot, before the React tree renders.
 */
export function installAssetPreloadRecovery(): void {
	window.addEventListener("vite:preloadError", (event) => {
		const now = Date.now();
		if (!shouldReloadAfterPreloadError(now, readLastReloadAt())) return;
		// Stops Vite re-throwing, so the rejection never reaches the error boundary.
		event.preventDefault();
		recordReloadAt(now);
		window.location.reload();
	});
}
